import type { OemContext } from '../../lib/auth';
import { permittedPage } from './route-catalog';
export const workspaces = [
  { key: 'factory', label: 'โรงงาน', english: 'Factory', representative: 'production' },
  { key: 'office', label: 'สำนักงาน', english: 'Office', representative: 'coordinator' },
  { key: 'customers', label: 'ลูกค้า', english: 'Customers', representative: 'customer-requester' },
  { key: 'owner', label: 'เจ้าของกิจการ', english: 'Owner', representative: 'owner' },
] as const;
export type WorkspaceKey = (typeof workspaces)[number]['key'];
export function workspaceGroup(context: Pick<OemContext, 'audience' | 'role'>): WorkspaceKey {
  if (context.audience === 'customer') return 'customers';
  if (context.role === 'owner') return 'owner';
  return ['rd', 'rd_checker', 'qa', 'production'].includes(context.role) ? 'factory' : 'office';
}
export function workspaceLabel(context: Pick<OemContext, 'audience' | 'role'>): string {
  return workspaces.find((group) => group.key === workspaceGroup(context))?.label ?? 'สำนักงาน';
}
export function audiencePath(path: string, audience: OemContext['audience']): string {
  if (!/^\/(?:app|portal)(?:\/|$)/.test(path)) return path;
  return path.replace(/^\/(?:app|portal)/, audience === 'customer' ? '/portal' : '/app');
}
export function canOpenPage(context: Pick<OemContext, 'audience'>, page: string): boolean {
  return permittedPage(context.audience, page);
}
export interface WorkspaceAction {
  page: string;
  label: string;
  icon: string;
}
export function homeActions(
  context: Pick<OemContext, 'audience' | 'role' | 'actions' | 'readBundles'>,
): WorkspaceAction[] {
  const group = workspaceGroup(context);
  const shared: WorkspaceAction[] = [
    { page: 'brief', label: 'ข้อมูลสินค้า', icon: 'product' },
    { page: 'development', label: 'พัฒนาจากสินค้าอ้างอิง', icon: 'product' },
    { page: 'samples', label: 'ตัวอย่างสินค้า', icon: 'sample' },
  ];
  const result =
    group === 'office'
      ? [
          ...shared,
          ...(context.readBundles.includes('design')
            ? [{ page: 'packaging', label: 'แบบบรรจุภัณฑ์', icon: 'package' }]
            : []),
          ...(context.readBundles.includes('commercial')
            ? [{ page: 'quotes', label: 'ราคาและเอกสาร', icon: 'document' }]
            : []),
          ...(context.readBundles.includes('finance')
            ? [{ page: 'payments', label: 'การชำระเงิน', icon: 'payment' }]
            : []),
          { page: 'work-list', label: 'ติดตามงาน', icon: 'calendar' },
        ]
      : group === 'factory'
        ? [
            ...shared,
            ...(context.readBundles.includes('technical')
              ? [{ page: 'feasibilities', label: 'กำลังการผลิต', icon: 'factory' }]
              : []),
            ...(context.actions.includes('review_technical') ||
            context.actions.includes('review_quality')
              ? [{ page: 'reviews', label: 'งานที่ต้องทบทวน', icon: 'review' }]
              : []),
            { page: 'handover', label: 'ข้อมูลส่งต่อ', icon: 'document' },
            { page: 'work-list', label: 'ติดตามงาน', icon: 'calendar' },
          ]
        : group === 'owner'
          ? [
              { page: 'reviews', label: 'งานที่ต้องตัดสินใจ', icon: 'review' },
              { page: 'quotes', label: 'ราคาและเอกสาร', icon: 'document' },
              { page: 'payments', label: 'การชำระเงิน', icon: 'payment' },
              { page: 'handover', label: 'ข้อมูลที่ยืนยันไว้', icon: 'package' },
              { page: 'work-list', label: 'ติดตามงาน', icon: 'calendar' },
            ]
          : [
              ...shared,
              ...(context.readBundles.includes('design')
                ? [{ page: 'artwork', label: 'แบบฉลากและบรรจุภัณฑ์', icon: 'package' }]
                : []),
              ...(context.readBundles.includes('commercial')
                ? [{ page: 'quotes', label: 'ราคาและเอกสาร', icon: 'document' }]
                : []),
              ...(context.readBundles.includes('finance')
                ? [{ page: 'payments', label: 'การชำระเงิน', icon: 'payment' }]
                : []),
              { page: 'work-list', label: 'ติดตามงาน', icon: 'calendar' },
            ];
  return result.filter((action) => permittedPage(context.audience, action.page)).slice(0, 6);
}
export function primaryNavigation(
  context: Pick<OemContext, 'audience' | 'role' | 'actions' | 'readBundles'>,
): WorkspaceAction[] {
  const actions = homeActions(context);
  return [
    { page: 'home', label: 'หน้าหลัก', icon: 'home' },
    ...actions.filter((a) => ['brief', 'quotes', 'reviews', 'work-list'].includes(a.page)),
  ].slice(0, 4);
}
